/**
 * Korin Studio — shared domain model.
 *
 * These types are the contract between the web client, the local backend and
 * future integrations (corporate SSO, MAM/MAM-like storage, AI workers).
 * Everything is plain JSON so it can travel over HTTP, SSE, webhooks or an
 * enterprise message bus without adapters.
 */

export type ID = string;
export type ISODate = string;

/* -------------------------------------------------------------------------- */
/*  Organisation & people                                                      */
/* -------------------------------------------------------------------------- */

export type OrgRole = "owner" | "admin" | "member";

export interface Organization {
  id: ID;
  name: string;
  slug: string;
  domain: string;
  accent: string;
  plan: "corporate" | "studio" | "trial";
  logoUrl?: string;
  createdAt: ISODate;
}

/** SaaS workspace identity. A user may belong to several workspaces. */
export type WorkspaceRole = "owner" | "admin" | "member" | "viewer";

export interface Workspace {
  id: ID;
  name: string;
  slug: string;
  role: WorkspaceRole;
  status: "active" | "read_only" | "suspended";
  planCode?: string;
  trialEndsAt?: ISODate;
}

export interface WorkspaceMember {
  id: ID;
  workspaceId: ID;
  userId: ID;
  role: WorkspaceRole;
  joinedAt: ISODate;
  lastAccessAt?: ISODate;
  user: {
    id: ID;
    name: string;
    email: string;
    avatarColor: string;
    avatarUrl?: string;
  };
}

export interface WorkspaceInvite {
  id: ID;
  workspaceId: ID;
  email: string;
  /** Owner cannot be invited: ownership is transferred, never granted by link. */
  role: Exclude<WorkspaceRole, "owner">;
  status: "pending" | "accepted" | "revoked" | "expired";
  createdAt: ISODate;
  expiresAt: ISODate;
  invitedBy?: { id: ID; name: string };
}

export interface WorkspaceInvitePreview {
  workspaceName: string;
  workspaceSlug: string;
  role: Exclude<WorkspaceRole, "owner">;
  email: string;
  expiresAt: ISODate;
  invitedBy?: string;
}

export interface WorkspaceUsage {
  planCode: string;
  planName: string;
  planStatus?: string;
  status?: "active" | "read_only" | "suspended";
  readOnly?: boolean;
  trialEndsAt?: ISODate;
  period?: string;
  seats: { used: number; pending: number; limit: number | null };
  projects: { used: number; limit: number | null };
  storageBytes: number;
  storageLimitBytes: number | null;
  aiCredits?: { used: number; limit: number | null };
  mediaMinutes?: { used: number; limit: number | null };
}

export interface BillingPlanEntitlements {
  seats: number | null;
  projects: number | null;
  storageBytes: number | null;
  aiCreditsPerMonth: number | null;
  mediaMinutesPerMonth: number | null;
  features: Record<string, boolean>;
}

export interface BillingPlan {
  code: string;
  name: string;
  priceUsdCents: number;
  annualUsdCents: number;
  interval: string;
  trialDays: number;
  sortOrder: number;
  entitlements: BillingPlanEntitlements;
}

export interface BillingCheckoutSession {
  id: string;
  provider: string;
  planCode: string;
  interval: "month" | "year";
  url: string;
  expiresAt: ISODate;
}

export interface User {
  id: ID;
  orgId: ID;
  name: string;
  email: string;
  phone?: string;
  title?: string;
  role: OrgRole;
  avatarColor: string;
  avatarAssetId?: ID;
  avatarUrl?: string;
  status: "active" | "invited" | "disabled";
  lastSeenAt?: ISODate;
  createdAt: ISODate;
}

export type UserSummary = Pick<
  User,
  "id" | "name" | "email" | "title" | "avatarColor" | "avatarUrl"
>;

export type ProjectRole =
  | "owner"
  | "producer"
  | "director"
  | "showrunner"
  | "writer"
  | "designer"
  | "editor"
  | "reviewer"
  | "viewer";

export interface ProjectMember {
  id: ID;
  projectId: ID;
  userId: ID;
  role: ProjectRole;
  user: UserSummary;
  createdAt: ISODate;
  online?: boolean;
}

export interface ProjectInvite {
  id: ID;
  projectId: ID;
  email: string;
  role: ProjectRole;
  status: "pending" | "accepted" | "expired" | "revoked";
  invitedBy?: UserSummary;
  createdAt: ISODate;
  expiresAt: ISODate;
}

/* -------------------------------------------------------------------------- */
/*  Projects, episodes, documents                                              */
/* -------------------------------------------------------------------------- */

export const PROJECT_FORMATS = [
  "series",
  "screenplay",
  "audioplay",
  "stageplay",
  "novel",
  "comic",
  "treatment",
  "text",
] as const;
export type ProjectFormat = (typeof PROJECT_FORMATS)[number];

export const PROJECT_STATUSES = [
  "development",
  "preproduction",
  "production",
  "postproduction",
  "delivered",
  "archived",
] as const;
export type ProjectStatus = (typeof PROJECT_STATUSES)[number];

export interface PageSetup {
  pageSize: "A4" | "Letter";
  fontFamily: "courier-prime" | "courier-new" | "plex-mono";
  fontSize: number;
  marginTop: number;
  marginBottom: number;
  marginLeft: number;
  marginRight: number;
  showSceneNumbers: boolean;
  numberPages: boolean;
  includeNotes: boolean;
  titlePage: boolean;
}

export interface ProjectSettings {
  page: PageSetup;
  revisionColor: RevisionColor;
  revisionMode: boolean;
  defaultDocumentId?: ID;
  aiHumanApproval: true;
  language: "ru" | "en";
  direction: "ltr" | "rtl";
  structureLabels: { act: string; sequence: string; scene: string };
  /** Preferred art-direction preset for concept boards and generated shots. */
  visualStyleId?: string;
}

export interface ProjectCover {
  color: string;
  accent: string;
  pattern: "signal" | "grid" | "arc" | "noise";
  assetId?: ID;
  url?: string;
}

export interface Project {
  id: ID;
  orgId: ID;
  title: string;
  format: ProjectFormat;
  status: ProjectStatus;
  logline: string;
  synopsis: string;
  genre: string;
  tags: string[];
  cover: ProjectCover;
  language: "ru" | "en";
  createdBy: ID;
  createdAt: ISODate;
  updatedAt: ISODate;
  archivedAt?: ISODate | null;
  settings: ProjectSettings;
  myRole?: ProjectRole;
  counts?: {
    documents: number;
    members: number;
    scenes: number;
    words: number;
    assets: number;
  };
}

export interface ProjectSummary extends Project {
  members?: ProjectMember[];
  progress?: number;
}

export type EpisodeStatus = "outline" | "draft" | "review" | "approved" | "locked";

export interface Episode {
  id: ID;
  projectId: ID;
  season: number;
  number: number;
  title: string;
  logline: string;
  summary: string;
  status: EpisodeStatus;
  dueDate?: ISODate;
  documentId?: ID;
  budget?: number;
  budgetCurrency: string;
  color: string;
  createdAt: ISODate;
  updatedAt: ISODate;
}

export type DocumentKind = ProjectFormat;

export interface DocumentSummary {
  id: ID;
  projectId: ID;
  title: string;
  kind: DocumentKind;
  episodeNumber?: number;
  order: number;
  status: "draft" | "review" | "approved" | "locked";
  stats: DocumentStats;
  revision: string;
  updatedAt: ISODate;
  updatedBy?: UserSummary;
  createdBy?: UserSummary;
  createdAt: ISODate;
}

export interface TitlePage {
  title: string;
  credit: string;
  authors: string[];
  source: string;
  draftDate: string;
  contact: string;
  copyright: string;
  extra: string;
}

export interface DocumentRecord extends DocumentSummary {
  content: ProseMirrorDoc;
  titlePage: TitlePage;
  settings?: Partial<PageSetup>;
  sceneOrder?: string[];
  lastSnapshotAt?: ISODate;
}

/* -------------------------------------------------------------------------- */
/*  Story structure                                                            */
/* -------------------------------------------------------------------------- */

export const STRUCTURE_KINDS = ["act", "sequence", "scene", "beat"] as const;
export type StructureKind = (typeof STRUCTURE_KINDS)[number];

export const STRUCTURE_STATUSES = [
  "idea",
  "outline",
  "draft",
  "revision",
  "review",
  "locked",
  "done",
] as const;
export type StructureStatus = (typeof STRUCTURE_STATUSES)[number];

export interface StructureNode {
  id: ID;
  projectId: ID;
  parentId?: ID | null;
  kind: StructureKind;
  title: string;
  summary: string;
  order: number;
  status: StructureStatus;
  color: string;
  tension?: number;
  beatType?: string;
  documentId?: ID | null;
  sceneBlockId?: string | null;
  locationId?: ID | null;
  characterIds: ID[];
  notes?: string;
  /**
   * Computed for production scenes: the scene block no longer exists in the
   * accepted revision of its document. The node is kept so boards and shots
   * are not lost; a human decides whether to archive or merge it.
   */
  sourceMissing?: boolean;
  /** References kept in the node but archived in the bible. */
  archivedCharacters?: Array<{ id: ID; name: string }>;
  archivedLocation?: { id: ID; name: string } | null;
  createdAt: ISODate;
  updatedAt: ISODate;
}

/* -------------------------------------------------------------------------- */
/*  Bibles: characters, locations, world                                       */
/* -------------------------------------------------------------------------- */

export const CHARACTER_ROLES = [
  "lead",
  "supporting",
  "guest",
  "antagonist",
  "mentor",
  "background",
] as const;
export type CharacterRole = (typeof CHARACTER_ROLES)[number];

export interface CharacterRelation {
  targetId: ID;
  type: string;
  note: string;
  strength: number;
}

export interface Character {
  id: ID;
  projectId: ID;
  name: string;
  fullName?: string;
  role: CharacterRole;
  age?: string;
  gender?: string;
  pronouns?: string;
  occupation?: string;
  oneLiner: string;
  bio: string;
  appearance: string;
  voice: string;
  goal: string;
  motivation: string;
  conflict: string;
  flaw: string;
  arc: string;
  backstory: string;
  secrets: string;
  tags: string[];
  color: string;
  avatarAssetId?: ID | null;
  avatarUrl?: string;
  relationships: CharacterRelation[];
  status: "active" | "archive";
  createdAt: ISODate;
  updatedAt: ISODate;
}

export interface Location {
  id: ID;
  projectId: ID;
  name: string;
  type: "interior" | "exterior" | "mixed" | "studio" | "virtual";
  description: string;
  timeOfDay: string;
  world: string;
  notes: string;
  tags: string[];
  imageAssetId?: ID | null;
  imageUrl?: string;
  color: string;
  createdAt: ISODate;
  updatedAt: ISODate;
}

/* -------------------------------------------------------------------------- */
/*  Media / assets                                                             */
/* -------------------------------------------------------------------------- */

export const ASSET_KINDS = ["image", "audio", "video", "document", "other"] as const;
export type AssetKind = (typeof ASSET_KINDS)[number];

/** A node of the production material tree (folders are virtual, not storage paths). */
export interface AssetFolder {
  id: ID;
  projectId: ID;
  parentId?: ID | null;
  name: string;
  kind: string;
  systemKey?: string | null;
  entityType?: "character" | "location" | "scene" | null;
  entityId?: ID | null;
  sortOrder: number;
  /** Direct files in this folder; descendants are aggregated by the client. */
  assetCount: number;
  createdAt: ISODate;
  updatedAt: ISODate;
}

export interface Asset {
  id: ID;
  projectId: ID;
  kind: AssetKind;
  filename: string;
  mime: string;
  size: number;
  url: string;
  thumbnailUrl?: string;
  width?: number;
  height?: number;
  duration?: number;
  storageKey: string;
  provider: "local" | "s3";
  tags: string[];
  folderId?: ID | null;
  linkedEntityType?: "character" | "location" | "project" | "document" | "scene" | null;
  linkedEntityId?: ID | null;
  metadata: Record<string, unknown>;
  uploadedBy?: UserSummary;
  createdAt: ISODate;
}

export interface UploadTicket {
  mode: "local" | "s3";
  uploadUrl: string;
  method: "PUT" | "POST";
  headers?: Record<string, string>;
  assetId: ID;
}

/* -------------------------------------------------------------------------- */
/*  Generated media (external providers: Kling AI, mock)                       */
/* -------------------------------------------------------------------------- */

export const GENERATION_KINDS = ["image", "video", "audio"] as const;
export type GenerationKind = (typeof GENERATION_KINDS)[number];

/**
 * "voice" covers both plain TTS and a ready clone profile; the exact mode is
 * stored in params.audio.mode. "lipsync" is a video result whose inputs are an
 * existing video and an audio track.
 */
export const GENERATION_PURPOSES = [
  "storyboard",
  "character",
  "location",
  "mood",
  "voice",
  "music",
  "sound",
  "lipsync",
  "talking",
] as const;
export type GenerationPurpose = (typeof GENERATION_PURPOSES)[number];

export const GENERATION_STATUSES = [
  "queued",
  "submitted",
  "processing",
  "succeeded",
  "failed",
  "cancelled",
] as const;
export type GenerationStatus = (typeof GENERATION_STATUSES)[number];

/* -------------------------------------------------------------------------- */
/*  Voice, music and lip-sync (future audio backend)                           */
/* -------------------------------------------------------------------------- */

export const AUDIO_MODES = ["tts", "clone", "music", "sfx"] as const;
export type AudioMode = (typeof AUDIO_MODES)[number];

export const VOICE_EMOTIONS = [
  "neutral",
  "warm",
  "joy",
  "sadness",
  "anger",
  "fear",
  "tender",
  "irony",
  "whisper",
  "shout",
] as const;
export type VoiceEmotion = (typeof VOICE_EMOTIONS)[number];

export type AudioFormat = "wav" | "mp3";

/**
 * Parameters for kind="audio". Voice cloning is never implicit: "clone" needs
 * a ready VoiceProfile whose consent was recorded on the server.
 */
export interface AudioGenerationParams {
  mode: AudioMode;
  language?: string;
  voiceProfileId?: ID;
  /** Human-readable voice name copied at request time (profile may change). */
  voiceName?: string;
  /** 0.5…2.0; applies to tts/clone. */
  speed?: number;
  format?: AudioFormat;
  /** Music/SFX length in seconds. */
  duration?: number;
  instrumental?: boolean;
  /** Scene voice cue that requested this TTS take, if any. */
  cueId?: ID;
  /** Shared id for a batch of cue takes requested from one scene. */
  batchId?: ID;
}

/** Parameters for purpose="lipsync"; the result kind is "video". */
export interface LipSyncParams {
  videoAssetId: ID;
  audioAssetId: ID;
  quality?: "standard" | "high";
}

/**
 * Audio-driven talking video: an image (a storyboard frame or concept) plus a
 * voice track and an optional performance prompt. The result is a new video
 * asset; neither input is overwritten.
 */
export interface TalkingVideoParams {
  imageAssetId: ID;
  audioAssetId: ID;
  prompt?: string;
  quality?: "standard" | "high";
  characterId?: ID;
  /** Optional voice cue whose audio was used as the driving track. */
  voiceCueId?: ID;
}

export const VOICE_PROFILE_STATUSES = ["draft", "processing", "ready", "failed", "revoked"] as const;
export type VoiceProfileStatus = (typeof VOICE_PROFILE_STATUSES)[number];

/**
 * A reusable cloned voice. The server keeps only the provider voice id and
 * the consent audit; raw samples stay in the project's media storage.
 */
export interface VoiceProfile {
  id: ID;
  projectId: ID;
  name: string;
  description: string;
  language: string;
  status: VoiceProfileStatus;
  /** Real provider id; never rendered in the browser. */
  provider: string;
  model: string;
  externalVoiceId?: string | null;
  /** Character this reference voice belongs to. */
  characterId?: ID | null;
  characterName?: string;
  /** Performance colour of this reference take. */
  emotion: VoiceEmotion;
  /** Transcript of the reference sample; required by several clone APIs. */
  referenceTranscript: string;
  /** Default voice for the character in this emotion. */
  isDefault: boolean;
  sampleAssetIds: ID[];
  samples: Asset[];
  consentConfirmedBy?: UserSummary;
  consentConfirmedAt?: ISODate;
  createdBy?: UserSummary;
  createdAt: ISODate;
  updatedAt: ISODate;
  error?: string | null;
}

export const VOICE_CUE_STATUSES = ["draft", "approved", "generating", "ready", "failed"] as const;
export type VoiceCueStatus = (typeof VOICE_CUE_STATUSES)[number];

/**
 * One character's voice choice for a scene frame. The same scene can have
 * several cues (different characters or different shots). The cue stores the
 * reference profile, the line to speak and, after TTS, the produced asset.
 */
export interface VoiceCue {
  id: ID;
  projectId: ID;
  documentId?: ID | null;
  sceneNodeId: ID;
  sceneBlockId?: string | null;
  sceneTitle?: string;
  /** Editorial shot this line belongs to; null on legacy scene + shot_key cues. */
  shotId?: ID | null;
  shotKey: string;
  shotLabel: string;
  characterId: ID;
  characterName?: string;
  voiceProfileId: ID;
  voiceProfileName?: string;
  emotion: VoiceEmotion;
  text: string;
  status: VoiceCueStatus;
  audioAssetId?: ID | null;
  audioAsset?: Asset | null;
  generationId?: ID | null;
  source: "human" | "ai";
  rationale: string;
  /** Every TTS attempt for this line, including the selected main take. */
  takes: VoiceCueTake[];
  createdBy?: UserSummary;
  createdAt: ISODate;
  updatedAt: ISODate;
}

/**
 * One TTS attempt attached to a voice cue. It is a normal media_generations
 * row linked by params.audio.cueId; selection is never automatic.
 */
export interface VoiceCueTake {
  generationId: ID;
  status: GenerationStatus;
  asset?: Asset | null;
  error?: string | null;
  errorCode?: string | null;
  provider: string;
  model: string;
  text: string;
  selected: boolean;
  createdAt: ISODate;
}

export type VoiceCueBatchStatus =
  | "queued"
  | "running"
  | "completed"
  | "partial"
  | "failed"
  | "cancelled";

export interface VoiceCueBatchItem {
  cueId: ID;
  generationId: ID;
  status: GenerationStatus;
  selected: boolean;
  error?: string | null;
}

/** Progress of one scene voiceover request, derived from its generations. */
export interface VoiceCueBatch {
  id: ID;
  projectId: ID;
  sceneNodeId: ID;
  status: VoiceCueBatchStatus;
  total: number;
  queued: number;
  running: number;
  succeeded: number;
  failed: number;
  cancelled: number;
  items: VoiceCueBatchItem[];
  createdAt: ISODate;
  updatedAt: ISODate;
}

export interface AudioProviderInfo {
  /** "gateway" is the private generator service behind the API. */
  provider: "mock" | "gateway";
  configured: boolean;
  external: boolean;
  capabilities: {
    tts: boolean;
    voiceClone: boolean;
    music: boolean;
    sfx: boolean;
    lipsync: boolean;
    talking: boolean;
    transcribe: boolean;
  };
  models: {
    tts: string;
    clone: string;
    music: string;
    sfx: string;
    lipsync: string;
    talking: string;
    transcribe: string;
  };
  hint?: string;
}

/** Result of speech-to-text for a reference sample. */
export interface TranscriptionResult {
  text: string;
  language: string;
  provider: string;
  /** True when the text is a stub, not a real recognition. */
  mock: boolean;
}

export interface GenerationReference {
  assetId: ID;
  entityType: "character" | "location";
  entityId: ID;
  role: "character" | "location";
  /** Human label shown in the prompt and in the generation card. */
  label: string;
  /** Kling input slot: image_1 … image_10. */
  slot: string;
}

export interface MediaElementBinding {
  /** Kling Element id from element_create. Referenced in the prompt as <<<id>>>. */
  id: string;
  /** Human-readable subject name bound to the id. */
  bindName: string;
  type: "image" | "video";
  /** Server-side provenance; not sent to Kling. */
  entityType?: "character" | "location";
  entityId?: string;
}

export interface MediaGenerationParams {
  aspectRatio: string;
  resolution: string;
  duration?: number;
  /**
   * Legacy batch key kept on every frame. New frames also carry the real
   * storyboard_boards link in MediaGeneration.storyboardBoardId.
   */
  batchId?: string;
  /** Storyboard board name copied for backward-compatible display. */
  storyboardTitle?: string;
  /** Cropped/imported frame; no generation was requested. */
  imported?: boolean;
  /** Scene that owns an imported storyboard frame. */
  sceneNodeId?: string;
  /** Storyboard batch: human label of the shot (Общий, Средний, Крупный…). */
  shotLabel?: string;
  /** Shared art-direction suffix applied to every shot. */
  style?: string;
  /** Project art-direction preset used for the final prompt. */
  styleId?: string;
  /** Approved concept references attached to this generation. */
  references?: GenerationReference[];
  /** Reusable Kling Element subjects used by this generation. */
  elements?: MediaElementBinding[];
  /** Concept board that produced this shot. */
  boardId?: string;
  /** Board shot key: portrait, sheet, expressions, face_*, establishing… */
  shotKey?: string;
  /** Project-wide storyboard batch marker; absent on single-scene boards. */
  batch?: StoryboardBatchMarker;
  /** Audio parameters; present when kind === "audio". */
  audio?: AudioGenerationParams;
  /** Lip-sync inputs; present when purpose === "lipsync". */
  lipsync?: LipSyncParams;
  /** Audio-driven talking-video inputs; present when purpose === "talking". */
  talking?: TalkingVideoParams;
}

/* -------------------------------------------------------------------------- */
/*  Storyboard boards                                                          */
/* -------------------------------------------------------------------------- */

export const STORYBOARD_BOARD_STATUSES = ["draft", "approved", "rejected", "archived"] as const;
export type StoryboardBoardStatus = (typeof STORYBOARD_BOARD_STATUSES)[number];

export const STORYBOARD_BOARD_SOURCES = ["generated", "imported"] as const;
export type StoryboardBoardSource = (typeof STORYBOARD_BOARD_SOURCES)[number];

/**
 * A named storyboard variant for one scene. Frames are ordinary
 * media_generations linked by storyboard_board_id; a board itself carries the
 * editorial state, ordering and the single "primary" choice for its scene.
 */
/**
 * Marker stored on every frame of a project-wide storyboard batch. The batch
 * has no separate queue table: frames are normal media_generations rows and
 * their latest attempt is the source of truth for progress.
 */
export interface StoryboardBatchMarker {
  id: ID;
  title: string;
  sceneId: ID;
  sceneTitle: string;
  sceneIndex: number;
  sceneCount: number;
  shotIndex: number;
  shotCount: number;
  createdAt: ISODate;
  /** Set only on a frame whose provider task was cancelled by a human. */
  cancelledAt?: ISODate | null;
  /** Previous generation id when this frame is a retry. */
  retryOf?: ID;
}

export const STORYBOARD_BATCH_STATUSES = ["queued", "running", "succeeded", "failed", "cancelled"] as const;
export type StoryboardBatchStatus = (typeof STORYBOARD_BATCH_STATUSES)[number];

export type StoryboardBatchSceneStatus = StoryboardBatchStatus;

export interface StoryboardBatchScene {
  sceneId: ID;
  title: string;
  boardId: ID;
  boardTitle: string;
  status: StoryboardBatchSceneStatus;
  totalFrames: number;
  queuedFrames: number;
  activeFrames: number;
  readyFrames: number;
  failedFrames: number;
  cancelledFrames: number;
  queueError?: string;
  error?: string;
}

/**
 * Progress of a project-wide storyboard run. A batch creates one draft board
 * per scene and queues every shot; a human still approves boards separately.
 */
export interface StoryboardBatch {
  id: ID;
  projectId: ID;
  title: string;
  status: StoryboardBatchStatus;
  scenes: StoryboardBatchScene[];
  totalFrames: number;
  queuedFrames: number;
  activeFrames: number;
  readyFrames: number;
  failedFrames: number;
  cancelledFrames: number;
  shotsPerScene: number;
  style: string;
  aspectRatio: string;
  resolution: string;
  createdAt: ISODate;
  updatedAt: ISODate;
  cancelledAt?: ISODate | null;
}

export interface StoryboardBoard {
  id: ID;
  projectId: ID;
  sceneNodeId: ID;
  sceneBlockId?: string | null;
  title: string;
  status: StoryboardBoardStatus;
  source: StoryboardBoardSource;
  sortOrder: number;
  isPrimary: boolean;
  frameCount: number;
  readyCount: number;
  styleId?: string | null;
  style: string;
  approvedBy?: UserSummary;
  approvedAt?: ISODate | null;
  archivedAt?: ISODate | null;
  createdBy?: UserSummary;
  createdAt: ISODate;
  updatedAt: ISODate;
}

export const CONCEPT_BOARD_STATUSES = ["draft", "approved", "rejected", "superseded"] as const;
export type ConceptBoardStatus = (typeof CONCEPT_BOARD_STATUSES)[number];

export const CONCEPT_BOARD_ELEMENT_STATUSES = ["none", "creating", "ready", "failed", "stale"] as const;
export type ConceptBoardElementStatus = (typeof CONCEPT_BOARD_ELEMENT_STATUSES)[number];

export const CONCEPT_BOARD_KINDS = ["character_sheet", "location_board"] as const;
export type ConceptBoardKind = (typeof CONCEPT_BOARD_KINDS)[number];

export interface ConceptBoardItem {
  id: ID;
  boardId: ID;
  shotKey: string;
  label: string;
  sortOrder: number;
  isPrimary: boolean;
  /** Generation status, or "missing" when the shot has not been requested yet. */
  status: GenerationStatus | "missing";
  error?: string | null;
  generationId?: ID | null;
  asset?: Asset | null;
}

export interface ConceptBoard {
  id: ID;
  projectId: ID;
  entityType: "character" | "location";
  entityId: ID;
  kind: ConceptBoardKind;
  title: string;
  status: ConceptBoardStatus;
  styleId: string;
  notes: string;
  items: ConceptBoardItem[];
  /** Best available image for the board: face 3/4 for characters, establishing for locations. */
  primaryAsset?: Asset | null;
  /** Approved and has the reference images needed for production shots. */
  referenceReady: boolean;
  /** Reusable Kling Element created from this board (video identity lock). */
  elementId?: string | null;
  elementStatus: ConceptBoardElementStatus;
  elementError?: string | null;
  elementCreatedAt?: string;
  approvedBy?: UserSummary;
  approvedAt?: string;
  createdBy?: UserSummary;
  createdAt: ISODate;
  updatedAt: ISODate;
}

export interface MediaGeneration {
  id: ID;
  projectId: ID;
  kind: GenerationKind;
  purpose: GenerationPurpose;
  prompt: string;
  negativePrompt: string;
  status: GenerationStatus;
  /** "kling" | "mock" — who actually answered, not just the configured provider. */
  provider: string;
  model: string;
  params: MediaGenerationParams;
  /** Real storyboard_boards link for storyboard frames; null for other media. */
  storyboardBoardId?: ID | null;
  /** Shot that owns this take; null for non-storyboard media. */
  shotId?: ID | null;
  taskId?: ID | null;
  asset?: Asset | null;
  error?: string | null;
  /** Machine-readable failure reason; "storage" means the provider result exists. */
  errorCode?: string | null;
  /** Number of storage writes attempted for this provider result. */
  storageAttempts?: number;
  /** When the provider first reported success, even if storage failed. */
  providerSucceededAt?: ISODate | null;
  linkedEntityType?: "character" | "location" | "scene" | null;
  linkedEntityId?: ID | null;
  createdBy?: ID | null;
  createdAt: ISODate;
  updatedAt: ISODate;
}

/* -------------------------------------------------------------------------- */
/*  Storyboard shots                                                           */
/* -------------------------------------------------------------------------- */

export const SHOT_STATUSES = [
  "draft",
  "queued",
  "generating",
  "ready",
  "failed",
  "cancelled",
] as const;
export type ShotStatus = (typeof SHOT_STATUSES)[number];

/**
 * A shot is the editorial frame inside one storyboard board. Every generation
 * attempt stays on the shot as a take; selectedGenerationId is the human's
 * choice of the main take and is never set automatically.
 */
export interface Shot {
  id: ID;
  projectId: ID;
  boardId: ID;
  /** Human board title; present when shots are listed across one scene. */
  boardTitle?: string;
  sceneNodeId: ID;
  sceneBlockId?: string | null;
  label: string;
  sortOrder: number;
  status: ShotStatus;
  selectedGenerationId?: ID | null;
  notes: string;
  generationCount: number;
  readyCount: number;
  /** Explicitly selected main take, if a human chose one. */
  selectedGeneration?: MediaGeneration | null;
  /** Best available take for a thumbnail; selection or latest success. */
  previewGeneration?: MediaGeneration | null;
  /** Full attempt history; present on GET /api/shots/:shotId. */
  attempts?: MediaGeneration[];
  createdBy?: UserSummary;
  createdAt: ISODate;
  updatedAt: ISODate;
}

/** A discussion comment attached to a shot, not to a screenplay document. */
export interface ShotComment {
  id: ID;
  projectId: ID;
  shotId: ID;
  author: UserSummary;
  body: string;
  mentions: ID[];
  resolved: boolean;
  resolvedBy?: UserSummary;
  resolvedAt?: ISODate | null;
  createdAt: ISODate;
  updatedAt: ISODate;
}

export const STORYBOARD_REVISION_TARGETS = ["shot", "storyboard_board"] as const;
export type StoryboardRevisionTarget = (typeof STORYBOARD_REVISION_TARGETS)[number];

export interface ShotRevisionSnapshot {
  kind: "shot";
  boardId: ID;
  sceneNodeId: ID;
  label: string;
  notes: string;
  sortOrder: number;
  selectedGenerationId: ID | null;
}

export interface BoardRevisionSnapshot {
  kind: "board";
  sceneNodeId: ID;
  title: string;
  status: StoryboardBoardStatus;
  isPrimary: boolean;
  sortOrder: number;
}

export type StoryboardRevisionSnapshot = ShotRevisionSnapshot | BoardRevisionSnapshot;

/** Immutable editorial snapshot; restoring creates another snapshot first. */
export interface StoryboardRevision {
  id: ID;
  projectId: ID;
  targetType: StoryboardRevisionTarget;
  targetId: ID;
  snapshot: StoryboardRevisionSnapshot;
  note: string;
  createdBy?: UserSummary;
  createdAt: ISODate;
}

export interface MediaProviderInfo {
  /** Display provider: "kling" covers both REST and MCP transports. */
  provider: "kling" | "mock";
  transport: "rest" | "mcp" | "mock";
  configured: boolean;
  /** MCP only: OAuth credentials were found in KLING_HOME. */
  authorized?: boolean;
  /** Human-readable next step (e.g. "выполните kling login"). */
  hint?: string;
  external: boolean;
  imageModel: string;
  videoModel: string;
  maxActive: number;
}

/* -------------------------------------------------------------------------- */
/*  Collaboration                                                              */
/* -------------------------------------------------------------------------- */

export interface Comment {
  id: ID;
  projectId: ID;
  documentId: ID;
  blockId?: string | null;
  parentId?: ID | null;
  rangeStart?: number;
  rangeEnd?: number;
  quote?: string;
  author: UserSummary;
  body: string;
  mentions: ID[];
  resolved: boolean;
  createdAt: ISODate;
  updatedAt: ISODate;
}

/**
 * A personal editor bookmark. Bookmarks are visible only to their owner and
 * never change the Yjs document; range positions are absolute ProseMirror
 * positions captured at creation time, with the quote kept for context.
 */
export interface DocumentBookmark {
  id: ID;
  projectId: ID;
  documentId: ID;
  userId: ID;
  blockId: string;
  rangeStart?: number;
  rangeEnd?: number;
  quote: string;
  label: string;
  createdAt: ISODate;
  updatedAt: ISODate;
}

export const TRASH_ENTITY_TYPES = [
  "document",
  "asset",
  "proposal",
  "shot",
  "storyboard_board",
  "episode",
  "character",
  "location",
  "scene",
  "task",
  "deliverable",
  "voice_cue",
  "voice_profile",
  "concept_board",
] as const;
export type TrashEntityType = (typeof TRASH_ENTITY_TYPES)[number];

/**
 * A soft-deleted project entity. The underlying row is preserved until the
 * owner permanently purges it, so a restore never depends on cached UI state.
 */
export interface TrashItem {
  id: ID;
  projectId: ID;
  entityType: TrashEntityType;
  entityId: ID;
  title: string;
  detail: string;
  entityKind?: string;
  size?: number;
  deletedAt: ISODate;
  deletedBy?: UserSummary;
  /** Groups one cascade; restoring a parent revives only this batch. */
  archiveBatchId?: string;
  /** Short-lived archive-scoped preview URL for an archived asset. */
  previewUrl?: string;
}

/** Human-readable cascade summary shown before permanent deletion. */
export interface TrashDependency {
  label: string;
  count: number;
}

export interface PresenceUser {
  id: ID;
  name: string;
  color: string;
  avatarUrl?: string;
  cursor?: { blockId: string; label: string };
  mode?: "edit" | "read" | "comment";
  activeDocumentId?: ID;
  lastSeenAt: number;
}

/**
 * Project-wide presence: who is working in the project right now, in which
 * document and in which mode. Ephemeral — derived from Yjs awareness states in
 * API memory and never persisted to SQLite.
 */
export interface ProjectPresenceUser {
  userId: ID;
  name: string;
  color: string;
  avatarUrl?: string;
  mode: "edit" | "read" | "comment";
  documentId: ID;
  documentTitle: string;
  lastSeenAt?: number;
}

export interface ProjectPresence {
  users: ProjectPresenceUser[];
  count: number;
  updatedAt: ISODate;
}

export interface Snapshot {
  id: ID;
  projectId: ID;
  documentId: ID;
  name: string;
  kind: "manual" | "auto" | "pre-restore";
  stats: DocumentStats;
  createdBy?: UserSummary;
  createdAt: ISODate;
  note?: string;
}

export interface SnapshotDetail extends Snapshot {
  content: ProseMirrorDoc;
  titlePage: TitlePage;
}

export interface Activity {
  id: ID;
  projectId: ID;
  actorType: "user" | "system" | "agent";
  actorName: string;
  actorColor: string;
  actorAvatarUrl?: string;
  action: string;
  summary: string;
  targetType?: string;
  targetId?: ID;
  meta?: Record<string, unknown>;
  createdAt: ISODate;
}

export interface Notification {
  id: ID;
  userId: ID;
  kind: "comment" | "mention" | "ai" | "invite" | "system" | "production";
  title: string;
  body: string;
  projectId?: ID;
  link?: string;
  read: boolean;
  createdAt: ISODate;
}

/* -------------------------------------------------------------------------- */
/*  AI writers room                                                            */
/* -------------------------------------------------------------------------- */

export type AiAgentTemplate =
  | "showrunner"
  | "character"
  | "dialogue"
  | "continuity"
  | "researcher"
  | "coverage"
  | "voice-director"
  | "custom";

/** Languages an agent's name and prompt can live in. Extend here, not per screen. */
export type AgentLocale = "ru" | "en" | "kk";

export type AgentTranslationOrigin = "preset" | "ai" | "manual";

/**
 * One saved language variant of an agent's visible copy and prompt. The base
 * ai_agents row stays the source of truth for its own language; variants are
 * never written back into it.
 */
export interface AiAgentTranslation {
  id: ID;
  agentId: ID;
  projectId: ID;
  locale: AgentLocale;
  name: string;
  role: string;
  tagline: string;
  persona: string;
  instructions: string;
  origin: AgentTranslationOrigin;
  sourceHash: string;
  translatedBy?: ID;
  createdAt: ISODate;
  updatedAt: ISODate;
}

export interface AiAgent {
  id: ID;
  projectId: ID;
  name: string;
  role: string;
  template: AiAgentTemplate;
  tagline: string;
  persona: string;
  instructions: string;
  provider: string;
  model: string;
  temperature: number;
  color: string;
  emoji: string;
  enabled: boolean;
  autonomy: "suggest";
  /** Language of name/role/tagline/persona/instructions in this row. */
  sourceLocale: AgentLocale;
  /** Language the agent reads and writes in; defaults to sourceLocale. */
  workLocale: AgentLocale;
  /** Saved variants for other languages. */
  translations?: AiAgentTranslation[];
  lastRunAt?: ISODate;
  createdAt: ISODate;
  updatedAt: ISODate;
}

export type AiProposalOperation =
  | "replace-block"
  | "insert-after"
  | "append-document"
  | "note"
  | "character-patch"
  | "breakdown"
  | "create-character"
  | "voice-cast";

/** One character-to-reference-voice choice proposed for a scene. */
export interface VoiceCastAssignment {
  sceneNodeId: ID;
  sceneBlockId?: string | null;
  characterId: ID;
  characterName: string;
  voiceProfileId: ID;
  voiceProfileName: string;
  emotion: VoiceEmotion;
  shotKey?: string;
  shotLabel?: string;
  /** Suggested line; the human can edit it before TTS. */
  text?: string;
  confidence: number;
  rationale: string;
}

export interface AiProposalPayload {
  kind: AiProposalOperation;
  blockId?: string;
  blocks?: ScriptBlockInput[];
  text?: string;
  markdown?: string;
  characterId?: ID;
  characterPatch?: Partial<Character>;
  character?: Partial<Character>;
  breakdown?: { label: string; value: string }[];
  targetLabel?: string;
  voiceCast?: VoiceCastAssignment[];
}

export interface AiProposal {
  id: ID;
  projectId: ID;
  runId?: ID | null;
  agentId: ID;
  agentName: string;
  agentColor: string;
  agentEmoji: string;
  documentId?: ID | null;
  documentTitle?: string;
  operation: AiProposalOperation;
  title: string;
  rationale: string;
  originalText?: string;
  proposedText?: string;
  payload: AiProposalPayload;
  status: "pending" | "accepted" | "rejected" | "applied";
  createdAt: ISODate;
  resolvedAt?: ISODate;
  resolvedBy?: UserSummary;
}

export interface AiRun {
  id: ID;
  projectId: ID;
  documentId?: ID | null;
  brief: string;
  focus: string;
  agentIds: ID[];
  status: "queued" | "running" | "completed" | "failed";
  proposalsCount: number;
  error?: string;
  createdAt: ISODate;
  completedAt?: ISODate;
}

export interface AiProviderInfo {
  id: string;
  label: string;
  kind: "mock" | "openai-compatible" | "ollama" | "corporate";
  model: string;
  configured: boolean;
  baseUrl?: string;
  note?: string;
}

/* -------------------------------------------------------------------------- */
/*  Production                                                                 */
/* -------------------------------------------------------------------------- */

export const PRODUCTION_STAGES = [
  "development",
  "script",
  "review",
  "voice",
  "sound",
  "edit",
  "delivery",
] as const;
export type ProductionStage = (typeof PRODUCTION_STAGES)[number];

export type ProductionStatus = "todo" | "in_progress" | "blocked" | "review" | "done";

export interface ProductionTask {
  id: ID;
  projectId: ID;
  episodeId?: ID | null;
  title: string;
  description: string;
  stage: ProductionStage;
  status: ProductionStatus;
  assigneeId?: ID | null;
  assignee?: UserSummary;
  dueDate?: ISODate | null;
  priority: "low" | "normal" | "high" | "critical";
  relatedSceneBlockId?: string | null;
  /** Computed: the linked production scene is archived while the task stays active. */
  relatedSceneArchived?: boolean;
  createdAt: ISODate;
  updatedAt: ISODate;
}

export interface Deliverable {
  id: ID;
  projectId: ID;
  episodeId?: ID | null;
  title: string;
  kind: "script" | "audio" | "video" | "comic" | "package" | "other";
  status: "planned" | "in_progress" | "review" | "approved" | "delivered";
  dueDate?: ISODate | null;
  assetId?: ID | null;
  /** Script deliverables point at a document; media deliverables keep assetId. */
  documentId?: ID | null;
  /** The artifact was handed over outside Korin and has no local file. */
  externalDelivery: boolean;
  notes: string;
  createdAt: ISODate;
  updatedAt: ISODate;
}

/* -------------------------------------------------------------------------- */
/*  Scene breakdown (production-facing script analysis)                        */
/* -------------------------------------------------------------------------- */

export const BREAKDOWN_CATEGORIES = [
  "props",
  "costume",
  "makeup",
  "vfx",
  "stunts",
  "extras",
  "vehicle",
  "sound",
  "other",
] as const;
export type BreakdownCategory = (typeof BREAKDOWN_CATEGORIES)[number];

export type BreakdownItemSource = "human" | "ai";

/** One production requirement attached to a scene of an accepted script. */
export interface BreakdownItem {
  id: ID;
  projectId: ID;
  documentId: ID;
  documentTitle: string;
  /** Block id of the scene heading in the document. */
  sceneBlockId: string;
  sceneNumber: number;
  /** Heading snapshot taken on creation; the scene itself can be renamed later. */
  sceneHeading: string;
  sceneLocation: string;
  sceneTimeOfDay: string;
  category: BreakdownCategory;
  name: string;
  quantity: number;
  note: string;
  source: BreakdownItemSource;
  /** Current scene from the accepted script; null when the heading is gone. */
  currentSceneNumber?: number | null;
  currentSceneHeading?: string | null;
  currentSceneLocation?: string | null;
  currentSceneTimeOfDay?: string | null;
  /** Snapshot differs from the current accepted scene. */
  isStale?: boolean;
  /** Linked production scene is in the archive. */
  isArchived?: boolean;
  /** Heading no longer exists in the accepted script. */
  sourceMissing?: boolean;
  createdAt: ISODate;
  updatedAt: ISODate;
}

/** A scene from the accepted script, used as the left-hand breakdown index. */
export interface BreakdownScene {
  documentId: ID;
  documentTitle: string;
  documentOrder: number;
  blockId: string;
  number: number;
  heading: string;
  location: string;
  timeOfDay: string;
  characters: string[];
  itemCount: number;
  quantity: number;
}

export interface BreakdownSummary {
  items: number;
  quantity: number;
  scenes: number;
  scenesWithItems: number;
  scenesWithoutItems: number;
  /** Items whose scene block is no longer present in the accepted script. */
  orphanedItems: number;
  byCategory: Record<BreakdownCategory, number>;
}

export interface ProjectBreakdown {
  generatedAt: ISODate;
  scenes: BreakdownScene[];
  items: BreakdownItem[];
  summary: BreakdownSummary;
}

/* -------------------------------------------------------------------------- */
/*  Integrations & platform                                                    */
/* -------------------------------------------------------------------------- */

export interface Integration {
  id: ID;
  orgId: ID;
  kind: string;
  name: string;
  description: string;
  category: "storage" | "ai" | "sso" | "messaging" | "post" | "api";
  status: "connected" | "available" | "error" | "disabled";
  config: Record<string, string>;
  hasSecret: boolean;
  docsUrl?: string;
  lastSyncAt?: ISODate;
  updatedAt: ISODate;
}

export interface ApiToken {
  id: ID;
  orgId: ID;
  name: string;
  prefix: string;
  scopes: string[];
  createdAt: ISODate;
  lastUsedAt?: ISODate;
  expiresAt?: ISODate;
}

export interface Webhook {
  id: ID;
  orgId: ID;
  url: string;
  events: string[];
  active: boolean;
  secretPrefix: string;
  lastStatus?: number;
  lastTriggeredAt?: ISODate;
  createdAt: ISODate;
}

/* -------------------------------------------------------------------------- */
/*  Stats                                                                      */
/* -------------------------------------------------------------------------- */

export interface CharacterLineStats {
  name: string;
  characterId?: ID;
  words: number;
  lines: number;
  scenes: number;
  color?: string;
}

export interface SceneStats {
  blockId: string;
  number: number;
  heading: string;
  words: number;
  dialogueWords: number;
  characters: string[];
  pageStart: number;
  estimatedSeconds: number;
  style?: string;
}

export interface DocumentStats {
  words: number;
  characters: number;
  charactersNoSpaces: number;
  pages: number;
  blocks: number;
  scenes: number;
  dialogueWords: number;
  actionWords: number;
  speakingCharacters: number;
  estimatedSeconds: number;
  readingMinutes: number;
  styles: Record<string, { blocks: number; words: number }>;
  charactersStats: CharacterLineStats[];
  scenesList: SceneStats[];
  titlePage? : boolean;
  updatedAt?: ISODate;
}

export interface ProjectStats {
  projects: number;
  documents: number;
  words: number;
  scenes: number;
  members: number;
  assets: number;
  weeklyWords: number;
  activeWriters: number;
}

export interface WritingDay {
  date: string;
  words: number;
  minutes: number;
}

export interface SearchResult {
  type: "document" | "character" | "location" | "asset" | "scene" | "note" | "project";
  id: ID;
  projectId: ID;
  projectTitle?: string;
  title: string;
  snippet: string;
  href: string;
}

/* -------------------------------------------------------------------------- */
/*  ProseMirror JSON (kept structurally minimal, not imported from PM)         */
/* -------------------------------------------------------------------------- */

export interface PMMark {
  type: string;
  attrs?: Record<string, unknown>;
}

export interface PMNode {
  type: string;
  attrs?: Record<string, unknown>;
  content?: PMNode[];
  marks?: PMMark[];
  text?: string;
}

export interface ProseMirrorDoc {
  type: "doc";
  content: PMNode[];
}

/** Lightweight block used by AI, importers and templates. */
export interface ScriptBlockInput {
  id?: string;
  style: ScreenplayStyle;
  text: string;
  revision?: RevisionColor;
  sceneNumber?: string | null;
}

export type ScreenplayStyle =
  | "sceneHeading"
  | "sceneCharacters"
  | "action"
  | "character"
  | "parenthetical"
  | "dialogue"
  | "lyrics"
  | "transition"
  | "shot"
  | "inlineNote"
  | "sequenceHeading"
  | "actHeading"
  | "beatHeading"
  | "unformattedText"
  | "pageBreak";

export type RevisionColor =
  | "none"
  | "blue"
  | "pink"
  | "green"
  | "yellow"
  | "goldenrod"
  | "buff"
  | "salmon"
  | "cherry"
  | "tan"
  | "gray";

/* -------------------------------------------------------------------------- */
/*  Auth & session DTOs                                                        */
/* -------------------------------------------------------------------------- */

export interface LoginRequest {
  identifier: string;
  password: string;
}

export interface AuthSession {
  accessToken: string;
  expiresAt: ISODate;
  user: User;
  /** Legacy alias for the active workspace; removed after the web migration. */
  org: Organization;
  orgs: { org: Organization; role: OrgRole }[];
  /** Present in cloud mode: every workspace the user may switch to. */
  workspaces?: { workspace: Workspace; role: WorkspaceRole }[];
  activeWorkspace?: Workspace;
}

export interface ProjectBundle {
  project: Project;
  members: ProjectMember[];
  invites: ProjectInvite[];
  documents: DocumentSummary[];
  episodes: Episode[];
  structure: StructureNode[];
  characters: Character[];
  locations: Location[];
  agents: AiAgent[];
  stats: ProjectStats;
  recentActivity: Activity[];
}

export interface ApiError {
  statusCode: number;
  error: string;
  message: string;
  details?: unknown;
}
